import * as React from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Input } from '@/shared/ui/input'

export interface PasswordInputProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  /** Whether the text is visible. Owned by the caller so two fields can share one flag. */
  revealed: boolean
  onRevealChange: (revealed: boolean) => void
  /** Accessible names of the toggle: what it WILL do ("Show password" while hidden). */
  showLabel: string
  hideLabel: string
  /**
   * `false` renders no eye of its own: the field follows another field's `revealed`. A "new
   * password" and its "confirm" share one toggle (reveal both, check they match), while a "current
   * password" keeps its own — revealing a typed-in new password must never reveal the old one.
   */
  showToggle?: boolean
  /** Skip the shared field styling — the dark register page paints its own inputs. */
  bare?: boolean
  toggleClassName?: string
  toggleStyle?: React.CSSProperties
}

/**
 * A password field with a show/hide eye. One component for every password field in the app, so the
 * eye reads the same everywhere and a change to it is made once.
 *
 * Convention (D77, decided 2026-10-10): the icon names the ACTION — an open eye while the text is
 * hidden ("click to show"), a crossed eye while it is visible — as Material, shadcn and GitHub do.
 * The button deliberately carries no {@code aria-pressed}: that is a STATE signal and contradicted
 * the action icon, which is what made one screen read as "reversed". The label alone says what a
 * click does.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  (
    {
      revealed, onRevealChange, showLabel, hideLabel, showToggle = true, bare = false,
      className, toggleClassName, toggleStyle, ...inputProps
    },
    ref,
  ) => {
    const type = revealed ? 'text' : 'password'
    return (
      <div className="relative">
        {bare
          ? <input ref={ref} type={type} className={className} {...inputProps} />
          : <Input ref={ref} type={type} className={cn(showToggle && 'pr-11', className)} {...inputProps} />}
        {showToggle && (
          <button
            type="button"
            tabIndex={-1}
            // 44 px on a phone (F28); the input's right padding leaves room for it.
            className={cn(
              'absolute right-0 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center text-muted-foreground hover:text-foreground max-sm:h-11 max-sm:w-11',
              toggleClassName,
            )}
            style={toggleStyle}
            aria-label={revealed ? hideLabel : showLabel}
            onClick={() => onRevealChange(!revealed)}
          >
            {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
    )
  },
)
PasswordInput.displayName = 'PasswordInput'
